// Traffic Fines: every notice issued to a Fleetly vehicle, who was proposed as
// the driver and why, who owns the car, and what has been decided. The history
// from the old fines sheet and the issued owner statements is on the same queue,
// marked as history.
//
// Every fact on this screen comes from Postgres (db/migrations/0340 to 0343):
// the order, days since the offence, the AARTO flag at 25 days, the proposals,
// the owner. The page arranges them. CLAUDE.md rule 4.
//
// Open to cockpit_access.can_manage_fines. The chosen fine is kept in the
// address (?f=), so a link to a fine opens it.
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AlertTriangle, FilePlus2, ShieldAlert, Ticket } from 'lucide-react'
import { useGetFine, useGetFinesAccess, useGetFinesOptions, useGetFinesQueue } from '../hooks/backend/fines'
import { Button } from '../lib/shadcn/button'
import { Input } from '../lib/shadcn/input'
import { NativeSelect } from '../lib/shadcn/native-select'
import { Checkbox } from '../lib/shadcn/checkbox'
import { Spinner } from '../lib/shadcn/spinner'
import { cn } from '../lib/shadcn/utils'
import { Chip } from '../components/interviews/Chip'
import { FinesList } from '../components/fines/FinesList'
import { FinePanel } from '../components/fines/FinePanel'
import { CaptureFineDialog } from '../components/fines/CaptureFineDialog'
import { STATUSES, formatZar, statusLabel, type FineDetail, type Options, type QueueResult } from '../utils/fines'

export default function TrafficFines() {
  const accessQuery = useGetFinesAccess<{ allowed: boolean }>()
  const queueQuery = useGetFinesQueue<QueueResult>()
  const fineQuery = useGetFine<FineDetail>()
  const optionsQuery = useGetFinesOptions<Options>()

  const [params, setParams] = useSearchParams()
  const selected = params.get('f')
  const [status, setStatus] = useState('')
  const [vehicle, setVehicle] = useState('')
  const [driver, setDriver] = useState('')
  const [owner, setOwner] = useState('')
  const [search, setSearch] = useState('')
  const [debounced, setDebounced] = useState('')
  const [history, setHistory] = useState(true)
  const [capturing, setCapturing] = useState(false)
  const [detail, setDetail] = useState<FineDetail | null>(null)

  const loadAccess = accessQuery.trigger
  const loadQueue = queueQuery.trigger
  const loadFine = fineQuery.trigger
  const loadOptions = optionsQuery.trigger

  useEffect(() => {
    void loadAccess({}, { skipCache: true }).catch(() => undefined)
  }, [loadAccess])
  const allowed = accessQuery.data?.allowed === true

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(search.trim()), 300)
    return () => window.clearTimeout(id)
  }, [search])

  const refreshQueue = useCallback(() => {
    void loadQueue(
      {
        status: status || null, vehicle_id: vehicle || null, driver_id: driver || null, owner_id: owner || null,
        search: debounced || null, include_history: history,
      },
      { skipCache: true },
    ).catch(() => undefined)
  }, [loadQueue, status, vehicle, driver, owner, debounced, history])

  const refreshOptions = useCallback(() => {
    void loadOptions({}, { skipCache: true }).catch(() => undefined)
  }, [loadOptions])

  useEffect(() => { if (allowed) refreshQueue() }, [allowed, refreshQueue])
  useEffect(() => { if (allowed) refreshOptions() }, [allowed, refreshOptions])
  useEffect(() => {
    if (!allowed || !selected) { setDetail(null); return }
    setDetail(null)
    void loadFine({ id: selected }, { skipCache: true }).then((d) => setDetail(d ?? null)).catch(() => undefined)
  }, [allowed, selected, loadFine])

  const select = (id: string | null) => {
    const next = new URLSearchParams(params)
    if (id) next.set('f', id)
    else next.delete('f')
    setParams(next, { replace: false })
  }

  const rows = useMemo(() => queueQuery.data?.rows ?? [], [queueQuery.data])
  const selectedRow = rows.find((r) => r.id === selected) ?? null
  const options = optionsQuery.data ?? null
  const flagged = rows.filter((r) => r.aarto_flag).length
  const shownTotal = rows.reduce((s, r) => s + (r.amount_zar ?? 0), 0)

  if (accessQuery.loading && !accessQuery.data && !accessQuery.error) {
    return (
      <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
        <Spinner className="h-4 w-4" /> Checking access…
      </div>
    )
  }
  if (accessQuery.error) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="max-w-md space-y-3 rounded-lg border border-border bg-card p-6 text-center text-sm">
          <p className="text-rose-700 dark:text-rose-300">{accessQuery.error}</p>
          <Button size="sm" variant="outline" onClick={() => void loadAccess({}, { skipCache: true }).catch(() => undefined)}>Try again</Button>
        </div>
      </div>
    )
  }
  if (!allowed) {
    return (
      <div className="flex h-full items-center justify-center p-6">
        <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-border bg-card p-8 text-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
            <ShieldAlert className="h-6 w-6" />
          </div>
          <p className="text-sm font-medium text-foreground">Traffic fines are open to operations managers.</p>
          <p className="text-xs text-muted-foreground">If you should be one of them, ask an admin to switch on can_manage_fines for you in cockpit access.</p>
        </div>
      </div>
    )
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-card">
      <div className="flex flex-wrap items-center gap-3 border-b border-border px-4 py-3">
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[hsl(var(--brand-soft))] text-[hsl(var(--brand-soft-foreground))]">
          <Ticket className="h-5 w-5" />
        </div>
        <div className="min-w-[12rem] flex-1">
          <h1 className="text-lg font-semibold text-foreground">Traffic Fines</h1>
          <p className="text-xs text-muted-foreground">
            Notices issued to Fleetly vehicles. The queue proposes a driver from the roster; a manager confirms. Nothing here deducts from pay.
          </p>
        </div>
        {flagged > 0 && (
          <Chip tone="red" icon={<AlertTriangle className="h-3 w-3" />} className="whitespace-normal">{flagged} past 25 days, nominate before day 32</Chip>
        )}
        <Button variant="brand" size="sm" onClick={() => setCapturing(true)}>
          <FilePlus2 className="mr-1 h-4 w-4" /> Capture a notice
        </Button>
      </div>

      {/* On a phone with a fine open, the fine needs the height more than the
          filters do. They come back with the Back button. */}
      <div className={cn('grid-cols-2 items-center gap-2 border-b border-border px-4 py-2 sm:flex sm:flex-wrap', selected ? 'hidden md:flex' : 'grid')}>
        <NativeSelect className="h-8 w-full sm:w-40 text-xs" value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
          <option value="">Every status</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>{statusLabel(s)}{options?.counts?.[s] !== undefined ? ` (${options.counts[s]})` : ''}</option>
          ))}
        </NativeSelect>
        <NativeSelect className="h-8 w-full sm:w-36 text-xs" value={vehicle} onChange={(e) => setVehicle(e.target.value)} aria-label="Vehicle">
          <option value="">Every vehicle</option>
          {(options?.vehicles ?? []).map((v) => <option key={v.id} value={v.id}>{v.registration}</option>)}
        </NativeSelect>
        <NativeSelect className="h-8 w-full sm:w-44 text-xs" value={driver} onChange={(e) => setDriver(e.target.value)} aria-label="Driver">
          <option value="">Every driver</option>
          {(options?.drivers ?? []).map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
        </NativeSelect>
        <NativeSelect className="h-8 w-full sm:w-44 text-xs" value={owner} onChange={(e) => setOwner(e.target.value)} aria-label="Owner">
          <option value="">Every owner</option>
          {(options?.owners ?? []).map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
        </NativeSelect>
        <Input className="h-8 w-full sm:w-48 text-xs" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Registration, notice or name" />
        <label className="inline-flex items-center gap-2 text-xs text-muted-foreground">
          <Checkbox checked={history} onCheckedChange={(v) => setHistory(v === true)} /> Include history
        </label>
        <span className="col-span-2 text-xs text-muted-foreground sm:ml-auto">
          {queueQuery.data ? `${rows.length} shown, ${formatZar(shownTotal)}` : ''}
        </span>
      </div>

      {queueQuery.error && (
        <div className="border-b border-border bg-rose-50 px-4 py-2 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-200">{queueQuery.error}</div>
      )}

      <div className="flex min-h-0 flex-1">
        <aside className={cn('min-h-0 w-full overflow-y-auto border-r border-border md:w-[26rem] md:shrink-0', selected ? 'hidden md:block' : 'block')}>
          {queueQuery.loading && !queueQuery.data ? (
            <p className="flex items-center justify-center gap-2 px-4 py-6 text-sm text-muted-foreground"><Spinner className="h-4 w-4" /> Loading the queue…</p>
          ) : (
            <FinesList rows={rows} selected={selected} onSelect={(id) => select(id)} empty="No fine matches these filters." />
          )}
        </aside>
        <section className={cn('min-h-0 min-w-0 flex-1 flex-col', selected ? 'flex' : 'hidden md:flex')}>
          <FinePanel
            detail={detail && detail.fine.id === selected ? detail : null}
            loading={fineQuery.loading}
            error={selected ? fineQuery.error ?? null : null}
            options={options}
            aartoFlag={selectedRow?.aarto_flag ?? null}
            onBack={() => select(null)}
            onChanged={(d) => { setDetail(d); refreshQueue(); refreshOptions() }}
            onDeleted={() => { select(null); refreshQueue(); refreshOptions() }}
          />
        </section>
      </div>

      <CaptureFineDialog
        open={capturing}
        onOpenChange={setCapturing}
        options={options}
        onSaved={(d) => { setDetail(d); select(d.fine.id); refreshQueue(); refreshOptions() }}
      />
    </div>
  )
}
